import { useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, InputAdornment, Typography } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../components/tables/DataTable';
import { Pagination } from '../../components/tables/Pagination';
import { PAGINATION_DEFAULT_PAGE_SIZE } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { getAllLocations, deleteLocation } from '../../services/locationService';
import type { LocationWithDetails } from '../../types/location';
import { locationsManageColumns } from './locationsManageColumns';
import { EditLocationManageDialog } from './EditLocationManageDialog';
import { DeleteLocationDialog } from './DeleteLocationDialog';
import { NewLocationManageDialog } from './NewLocationManageDialog';
import type { SiteLocationRow } from '../../types/project';

export const LocationsManagePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<LocationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('SiteName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_DEFAULT_PAGE_SIZE);
  const [editLocation, setEditLocation] = useState<LocationWithDetails | null>(null);
  const [deleteLocationState, setDeleteLocationState] = useState<SiteLocationRow | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getAllLocations());
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field: string) => {
    if (sortBy === field) setSortDir((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('asc'); }
  };

  const handleEditClick = (row: Record<string, unknown>) => {
    const loc = data.find((l) => l.LocationKey === row.LocationKey) ?? null;
    setEditLocation(loc);
  };

  const handleDeleteClick = (row: Record<string, unknown>) => {
    const loc = data.find((l) => l.LocationKey === row.LocationKey);
    if (!loc) return;
    setDeleteLocationState({
      LocationKey: loc.LocationKey, LocationId: loc.LocationId,
      LocationName: loc.LocationName, LocationType: loc.LocationType,
      ProjectKey: loc.ProjectKey, ProjectId: loc.ProjectId,
      ProjectName: loc.ProjectName, SampleCount: loc.SampleCount,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteLocationState) return;
    await deleteLocation(deleteLocationState.LocationKey);
    setDeleteLocationState(null);
    fetchData();
  };

  const filtered = data.filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return l.LocationId.toLowerCase().includes(q)
      || l.LocationName.toLowerCase().includes(q)
      || l.ProjectId.toLowerCase().includes(q)
      || l.ProjectName.toLowerCase().includes(q)
      || l.SiteName.toLowerCase().includes(q)
      || l.SiteCity.toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[sortBy];
    const bVal = (b as unknown as Record<string, unknown>)[sortBy];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 800, color: 'text.primary', letterSpacing: '-0.025em',
          fontSize: '1.75rem', lineHeight: 1.2,
        }}>
          Locations
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search locations..."
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
          }}
          sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.8125rem' } }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setNewOpen(true)}
          size="small" sx={{
            boxShadow: '0 2px 8px rgba(74,103,65,0.2)', borderRadius: '10px',
            textTransform: 'none', fontWeight: 600, px: 2.5, py: 0.75,
            fontSize: '0.8125rem',
          }}>
          New Location
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filtered.length} location{filtered.length !== 1 ? 's' : ''} across{' '}
        {new Set(filtered.map((l) => l.ProjectKey)).size} project(s)
      </Typography>

      <DataTable
        columns={locationsManageColumns}
        data={sorted.slice(page * pageSize, (page + 1) * pageSize) as unknown as Record<string, unknown>[]}
        loading={loading}
        onRowClick={(row) => {
          const projectId = row.ProjectId as string;
          if (projectId) navigate(ROUTES.LOCATIONS.replace(':projectId', projectId));
        }}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
        entityLabel="locations"
      />

      <EditLocationManageDialog
        open={!!editLocation}
        location={editLocation}
        onClose={() => setEditLocation(null)}
        onUpdated={() => { setEditLocation(null); fetchData(); }}
      />
      <NewLocationManageDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => { setNewOpen(false); fetchData(); }}
      />
      <DeleteLocationDialog
        open={!!deleteLocationState}
        location={deleteLocationState}
        onClose={() => setDeleteLocationState(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};
